import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  type BriefContent,
  EMPTY_BRIEF_CONTENT,
  normalizeBriefContent,
  useCreateSmartBriefWithContent,
  useDeleteSmartBrief,
  useDeleteSmartBriefLogo,
  useDeleteSmartBriefProductThumbnail,
  useSmartBriefContent,
  useUpdateSmartBrief,
  useUpdateSmartBriefContent,
  useUploadSmartBriefLogo,
  useUploadSmartBriefProductThumbnail,
} from "@/api/smart-briefs";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as showError, success } from "@/providers/ToastProvider";
import { BRIEF_MODAL_DIMENSIONS } from "@/utils/layout-constants";
import type { VideoExample } from "./SmartBriefContent";
import { SmartBriefEditInner } from "./SmartBriefEditInner";

/** Draft data passed from ProductSelectionModal for newly generated briefs */
export interface DraftBriefData {
  content: BriefContent;
  examples: VideoExample[];
  productName: string;
  productThumbnail?: string;
}

interface SmartBriefEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** For edit mode: the brief ID to edit */
  briefId?: string;
  productName?: string;
  /**
   * Called after successful save. If provided, onClose will NOT be called automatically.
   * Use this to navigate to the brief view after first save.
   */
  onSaveComplete?: (briefId: string) => void;
  /**
   * Draft data for newly generated briefs.
   * When provided, the modal operates in "draft mode" - no DB record exists yet.
   * Saving will create the brief record with this content.
   */
  draftData?: DraftBriefData;
}

export const SmartBriefEditModal = ({
  isOpen,
  onClose,
  briefId,
  productName,
  onSaveComplete,
  draftData,
}: SmartBriefEditModalProps) => {
  const navigate = useNavigate();
  const merchant = useMerchant();

  // Determine if we're in draft mode (no briefId, have draftData)
  const isDraftMode = !briefId && !!draftData;

  // Local state for editing
  const [editableContent, setEditableContent] = useState<BriefContent | null>(null);
  const [editableTitle, setEditableTitle] = useState("");
  const [editableBannerColor, setEditableBannerColor] = useState("#F3F3F3");
  // null = explicitly deleted, undefined = not set (use source data)
  const [editableProductThumbnail, setEditableProductThumbnail] = useState<string | null | undefined>(undefined);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset state when briefId changes (new brief opened) or draftData changes
  useEffect(() => {
    if (isDraftMode && draftData) {
      // Initialize from draft data
      const normalized = normalizeBriefContent(draftData.content as unknown as Record<string, unknown>);
      setEditableContent(normalized);
      setEditableTitle(`Brief for ${draftData.productName}`);
      setEditableBannerColor("#F3F3F3");
      setEditableProductThumbnail(draftData.productThumbnail);
      setHasChanges(false);
    } else {
      // Reset for edit mode
      setEditableContent(null);
      setEditableTitle("");
      setEditableBannerColor("#F3F3F3");
      setEditableProductThumbnail(undefined);
      setHasChanges(false);
    }
  }, [briefId, isDraftMode, draftData]);

  // API hooks - only fetch when modal is open AND in edit mode (not draft)
  const {
    data: briefData,
    isLoading,
    error,
    isFetched,
  } = useSmartBriefContent(
    isOpen && !isDraftMode && briefId ? briefId : "", // Disable query in draft mode
    productName || "All Products",
  );

  // Mutation hooks
  const createBriefWithContent = useCreateSmartBriefWithContent();
  const updateContent = useUpdateSmartBriefContent();
  const updateBrief = useUpdateSmartBrief();
  const uploadLogo = useUploadSmartBriefLogo();
  const deleteLogo = useDeleteSmartBriefLogo();
  const uploadProductThumbnail = useUploadSmartBriefProductThumbnail();
  const deleteProductThumbnail = useDeleteSmartBriefProductThumbnail();
  const deleteBrief = useDeleteSmartBrief();

  // Initialize editable state from fetched data (edit mode only)
  useEffect(() => {
    // Skip if in draft mode - already initialized from draftData
    if (isDraftMode) return;

    // Only run when we have fetched data and haven't set content yet
    if (!isFetched || editableContent) return;

    if (briefData?.content) {
      // Normalize the content to handle both old and new schemas
      const normalized = normalizeBriefContent(briefData.content as unknown as Record<string, unknown>);
      setEditableContent(normalized);
    } else {
      // No content available - use empty structure
      setEditableContent({ ...EMPTY_BRIEF_CONTENT });
    }

    if (briefData?.brief?.title) {
      setEditableTitle(briefData.brief.title);
    }
    if (briefData?.brief?.bannerColor) {
      setEditableBannerColor(briefData.brief.bannerColor);
    }
  }, [briefData, isFetched, editableContent, isDraftMode]);

  // Handle content changes
  const handleContentChange = (content: BriefContent) => {
    setEditableContent(content);
    setHasChanges(true);
  };

  // Handle title changes
  const handleTitleChange = (title: string) => {
    setEditableTitle(title);
    setHasChanges(true);
  };

  // Handle banner color changes
  const handleBannerColorChange = (color: string) => {
    setEditableBannerColor(color);
    setHasChanges(true);
  };

  // Handle product thumbnail upload
  // In edit mode: upload to S3 immediately
  // In draft mode: create blob URL for preview (will be uploaded when brief is saved)
  const handleProductThumbnailUpload = async (file: File) => {
    if (briefId) {
      // Edit mode: upload to S3 immediately
      try {
        const result = await uploadProductThumbnail.mutateAsync({
          briefId,
          thumbnailFile: file,
        });
        // Update local state with the S3 URL
        setEditableProductThumbnail(result.productThumbnail);
        success("Product thumbnail uploaded successfully!");
      } catch (err) {
        console.error("Failed to upload product thumbnail:", err);
        showError("Failed to upload product thumbnail");
      }
    } else {
      // Draft mode: create blob URL for preview
      const objectUrl = URL.createObjectURL(file);
      setEditableProductThumbnail(objectUrl);
      setHasChanges(true);
    }
  };

  // Handle product thumbnail delete
  // Deferred until save - just sets local state to null
  const handleProductThumbnailDelete = () => {
    // Revoke the old object URL if it was one we created
    if (editableProductThumbnail?.startsWith("blob:")) {
      URL.revokeObjectURL(editableProductThumbnail);
    }

    // Set to null to indicate explicit deletion (will be persisted on save)
    setEditableProductThumbnail(null);
    setHasChanges(true);
  };

  // Handle product thumbnail reset (restore auto-fetched thumbnail from product data)
  // In edit mode: clears the stored custom thumbnail via API
  // In draft mode: resets to original draftData thumbnail
  const handleProductThumbnailReset = async () => {
    // Revoke the old object URL if it was one we created
    if (editableProductThumbnail?.startsWith("blob:")) {
      URL.revokeObjectURL(editableProductThumbnail);
    }

    if (briefId) {
      // Edit mode: call API to clear stored thumbnail
      try {
        await deleteProductThumbnail.mutateAsync({
          briefId,
        });
        // Set to undefined (not null) to fall back to source data (auto-fetched thumbnail)
        setEditableProductThumbnail(undefined);
        success("Thumbnail reset to original");
      } catch (err) {
        console.error("Failed to reset product thumbnail:", err);
        showError("Failed to reset thumbnail");
      }
    } else {
      // Draft mode: reset to original draftData thumbnail
      setEditableProductThumbnail(undefined);
      setHasChanges(true);
    }
  };

  // Handle logo upload (only available in edit mode)
  const handleLogoUpload = async (file: File) => {
    if (!briefId) return; // Only in edit mode
    try {
      await uploadLogo.mutateAsync({
        briefId,
        logoFile: file,
      });
      success("Logo uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload logo:", err);
      showError("Failed to upload logo");
    }
  };

  // Handle logo delete (only available in edit mode)
  const handleLogoDelete = async () => {
    if (!briefId) return; // Only in edit mode
    try {
      await deleteLogo.mutateAsync({
        briefId,
      });
      success("Logo removed");
    } catch (err) {
      console.error("Failed to delete logo:", err);
      showError("Failed to remove logo");
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!editableContent) return;

    // Determine the product thumbnail to save:
    // - null = explicitly deleted (clear in DB)
    // - blob URL = local preview only, can't save (skip)
    // - remote URL = save it
    // - undefined = not changed (skip)
    const getProductThumbnailToSave = (): string | null | undefined => {
      if (editableProductThumbnail === null) return null; // Clear
      if (editableProductThumbnail === undefined) return undefined; // Not changed
      if (editableProductThumbnail.startsWith("blob:")) return undefined; // Can't save blob URLs
      return editableProductThumbnail; // Valid URL
    };

    try {
      if (isDraftMode && draftData) {
        // Draft mode: Create new brief with content
        const thumbnailToSave = getProductThumbnailToSave();
        const newBrief = await createBriefWithContent.mutateAsync({
          productName: draftData.productName,
          title: editableTitle,
          content: editableContent,
          // Only include productThumbnail if it's a valid value to save
          ...(thumbnailToSave !== undefined && { productThumbnail: thumbnailToSave ?? undefined }),
        });

        success("Brief created successfully!");
        setHasChanges(false);

        // Navigate to the new brief's view
        if (onSaveComplete) {
          onSaveComplete(newBrief.id);
        } else {
          onClose();
          navigate({
            to: "/merchant/$merchantId/smart-briefs/brief/$briefId",
            params: { merchantId: merchant, briefId: newBrief.id },
            search: { edit: false },
            replace: true,
          });
        }
      } else if (briefId) {
        // Edit mode: Update existing brief
        await updateContent.mutateAsync({
          briefId,
          productName: productName || "All Products",
          content: editableContent,
        });

        // Save title, banner color, and product thumbnail if changed
        const titleChanged = editableTitle && editableTitle !== briefData?.brief?.title;
        const colorChanged = editableBannerColor !== briefData?.brief?.bannerColor;
        const thumbnailToSave = getProductThumbnailToSave();
        const thumbnailChanged = thumbnailToSave !== undefined;
        const thumbnailCleared = thumbnailToSave === null;

        // Handle thumbnail deletion explicitly via delete endpoint
        if (thumbnailCleared) {
          await deleteProductThumbnail.mutateAsync({
            briefId,
          });
        }

        // Update other fields (title, banner color, non-null thumbnail)
        const hasOtherUpdates = titleChanged || colorChanged || (thumbnailChanged && !thumbnailCleared);
        if (hasOtherUpdates) {
          await updateBrief.mutateAsync({
            briefId,
            title: titleChanged ? editableTitle : undefined,
            bannerColor: colorChanged ? editableBannerColor : undefined,
            productThumbnail: thumbnailChanged && !thumbnailCleared ? thumbnailToSave : undefined,
          });
        }

        success("Brief saved successfully!");
        setHasChanges(false);

        // If onSaveComplete is provided (e.g., for newly created briefs), call it instead of onClose
        if (onSaveComplete) {
          onSaveComplete(briefId);
        } else {
          onClose();
        }
      }
    } catch (err) {
      console.error("Failed to save brief:", err);
      showError("Failed to save brief");
    }
  };

  // Handle delete (only available in edit mode, not draft mode)
  const handleDelete = async () => {
    if (isDraftMode || !briefId) {
      // In draft mode, just close - nothing saved to delete
      onClose();
      return;
    }

    try {
      await deleteBrief.mutateAsync(briefId);
      success("Brief deleted successfully!");
      onClose();
      navigate({ to: "/merchant/$merchantId/smart-briefs", params: { merchantId: merchant }, replace: true });
    } catch (err) {
      console.error("Failed to delete brief:", err);
      showError("Failed to delete brief");
    }
  };

  // Handle escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  // Handle backdrop click to close modal
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      // Only close if clicking directly on the backdrop, not the modal content
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  // Lock body scroll and add escape key listener when modal is open
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = "hidden";
      // Add escape key listener
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      // Restore body scroll
      document.body.style.overflow = "";
      // Remove escape key listener
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open OR if neither briefId nor draftData is available
  if (!isOpen || (!briefId && !draftData)) return null;

  // Loading state - show spinner while content is being initialized
  // In edit mode: wait for API fetch. In draft mode: wait for useEffect to set content from draftData
  if (isLoading || !editableContent) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 overflow-hidden"
        onClick={handleBackdropClick}
      >
        <div
          className={`relative bg-white rounded-lg ${BRIEF_MODAL_DIMENSIONS.SIZE} overflow-hidden flex items-center justify-center`}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-solid border-t-transparent" />
            <p className="text-gray-600">Loading brief...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (only for edit mode)
  if (!isDraftMode && error) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 overflow-hidden"
        onClick={handleBackdropClick}
      >
        <div
          className={`relative bg-white rounded-lg ${BRIEF_MODAL_DIMENSIONS.SIZE} overflow-hidden flex items-center justify-center`}
        >
          <div className="flex flex-col items-center gap-4">
            <p className="text-red-600">Error loading brief</p>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine which data to use
  const examples = isDraftMode ? draftData?.examples || [] : briefData?.examples || [];
  // Use editable thumbnail if set, otherwise fall back to source data
  // null = explicitly deleted (show no thumbnail), undefined = use source data
  const productThumbnail =
    editableProductThumbnail === null
      ? undefined // User explicitly deleted the thumbnail
      : (editableProductThumbnail ??
        (isDraftMode
          ? draftData?.productThumbnail
          : (briefData?.brief as { productThumbnail?: string })?.productThumbnail));
  const logoUrl = isDraftMode ? undefined : briefData?.brief?.logoUrl;
  const createdAt = isDraftMode ? new Date().toISOString() : briefData?.brief?.createdAt || new Date().toISOString();
  const isSaving = isDraftMode ? createBriefWithContent.isPending : updateContent.isPending || updateBrief.isPending;

  // Check if user has changed the thumbnail from the original
  // This determines whether to show the "Reset to original" button
  // In edit mode: true if there's a stored custom thumbnail in DB
  // In draft mode: true if user has changed from the original draftData thumbnail
  const hasCustomThumbnail = isDraftMode
    ? editableProductThumbnail !== undefined && editableProductThumbnail !== draftData?.productThumbnail
    : !!briefId && !!(briefData?.brief as { productThumbnail?: string })?.productThumbnail;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 overflow-hidden"
      onClick={handleBackdropClick}
    >
      {/* Large modal container - same dimensions as other brief modals */}
      <div className={`relative bg-white rounded-lg ${BRIEF_MODAL_DIMENSIONS.SIZE} overflow-hidden flex flex-col`}>
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <SmartBriefEditInner
            content={editableContent}
            title={editableTitle}
            bannerColor={editableBannerColor}
            logoUrl={logoUrl ?? undefined}
            examples={examples}
            createdAt={createdAt}
            productThumbnail={productThumbnail ?? undefined}
            onContentChange={handleContentChange}
            onTitleChange={handleTitleChange}
            onBannerColorChange={handleBannerColorChange}
            onLogoUpload={isDraftMode ? undefined : handleLogoUpload}
            onLogoDelete={isDraftMode ? undefined : handleLogoDelete}
            onProductThumbnailUpload={handleProductThumbnailUpload}
            onProductThumbnailDelete={handleProductThumbnailDelete}
            hasCustomThumbnail={hasCustomThumbnail}
            onProductThumbnailReset={hasCustomThumbnail ? handleProductThumbnailReset : undefined}
            onSave={handleSave}
            onCancel={onClose}
            onDelete={isDraftMode ? undefined : handleDelete}
            isSaving={isSaving}
            isDeleting={deleteBrief.isPending}
            isDraftMode={isDraftMode}
          />
        </div>
      </div>
    </div>
  );
};
